import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { standaloneLeaderboardPath } from "../../../utils/leaguePath";
import { toast } from "react-toastify";
import { getGameFromSlug, joingame } from "../../../api/user";
import { primeLeagueContext } from "../../../utils/useLeagueContext";
import LeagueInvitePreviewCard, { RuleCard } from "./LeagueInvitePreviewCard";
import PublicLeagueJoinCard from "./PublicLeagueJoinCard";
import CashGameLandingPage from "./CashGameLandingPage";
import { PodiumRewardsCard } from "./PodiumBadges";
import LeagueDetailRows from "./LeagueDetailRows";
import LeagueJoinedCelebrationModal from "./LeagueJoinedCelebrationModal";
import { isPendingApproval, leagueCtaState, stripHtml } from "./leagueStatus";
// The schedule, the rules and their glyphs now come from LeagueDetailRows, the
// block this page shares with the public landing page.
import {
  GlobeIcon,
  InvitePeopleIcon,
  SmallLockIcon,
  TrophyOutlineIcon,
} from "./icons";

// How long "You're in" holds before the league opens. The design brief puts this
// at 1.0-1.3s with a hard 2s ceiling: long enough to register as a moment, short
// enough that it never reads as loading.
const CELEBRATION_MS = 1200;

// Join refusals arrive as the server's lang/en/message.php text; each known one
// has its own copy, anything else gets the generic line.
const JOIN_ERRORS = {
  "You are already member of this game.": "You are already in this league.",
  "Game not found.": "League not found.",
  "Entry closed — this competition has already started.": "Entry is closed.",
};
const joinErrorMessage = (serverMessage) =>
  JOIN_ERRORS[serverMessage] || "Could not join this league. Try again.";

// What a league looks like from the outside, for anyone who isn't in it yet.
//
// The two kinds are genuinely different pitches, so they get different layouts and
// this component owns the join itself — the request, the failure toast and the
// celebration are the same either way.
//
// PRIVATE: an invite. It leads with the league's art and "You Found the League",
// because getting here meant holding a code, and the reveal is the point.
//
// PUBLIC: a listing. Nothing is being revealed — the league is on League Play for
// anyone to read — so PublicLeagueJoinCard opens on the name and argues for
// joining instead.
const COPY = {
  private: {
    icon: <SmallLockIcon className="w-5 h-5" />,
    title: "Private League",
    // Client Rev 4: "Private league card still wrapped. change the text copy on
    // both mobile and desktop to: 'Trade, post, compete privately' (no period)."
    //
    // Rev 3's shorter line ("Trade, post, compete with your group") was itself an
    // attempt to stop "group" wrapping, and it still wrapped — the card is one of
    // two side-by-side columns on desktop, so its text column is far narrower than
    // the card looks. This is the client's own replacement, four words shorter,
    // and it is the copy for BOTH breakpoints. No full stop, as asked.
    lead: "Trade, post, compete privately",
    // Client Rev 2: "The three Private League check lines currently end
    // inconsistently. 'Only members can see inside.' has a period while the other
    // two do not. I would remove that period and treat all three as compact list
    // statements." The other two come from leagueFormat and never carried one.
    closing: "Only members can see inside",
  },
  public: {
    icon: <GlobeIcon className="w-5 h-5" stroke="#22A36B" />,
    title: "Public League",
    lead: "Open to all QUSHi traders.",
    closing: "Anyone on QUSHi can join.",
  },
};

// The join CTA and the line above it, rendered twice: once in the card's footer
// on desktop, once in the docked bar on mobile. One component so the label, the
// disabled states and the handler cannot diverge between the two.
//
// Client: "Add 'Free to Join' above the CTA, just like the public leagues CTA."
// The public league card's line is `text-center text-sm text-secondary` reading
// "Free to join", and it says "Entry required" instead when the league carries a
// funded cash prize — a private league can too, so the same condition comes with
// it. Taking the public card's exact treatment is what "just like public leagues"
// asks for, which is also why the string is its lower-case "Free to join" rather
// than the title case of the request.
// The supporting line and the one action, as a pair.
//
// Client Rev4 (0014888 item 4): "Muted, regular weight Free to join / Full
// width Join League CTA / Both elements remain sticky together above the
// mobile navigation... Only show 'Free to join' while the user is eligible to
// join and the CTA says Join League. If the CTA changes to another state, the
// supporting line should disappear with it."
//
// So the line is derived FROM the button's label rather than from a second
// reading of the same state. "View League" and "Joining…" are not offers, and
// a line promising the league is free under a button that says "View League"
// is answering a question nobody asked. Deriving it means a future CTA state
// hides the line automatically instead of needing this condition updated.
export const JoinBlock = ({ game, joining, disabled, onJoin }) => {
  const navigate = useNavigate();
  // The four states, from the one definition every landing CTA reads — see
  // leagueCtaState. The dead "Entry closed" is gone: a live public league now
  // offers its leaderboard, which is where the listing row already sends people.
  const state = leagueCtaState(game);
  // "offering" is the state that makes a promise — it drives the supporting
  // line above the button and the disabled rule. A blocked Entry closed is an
  // answer, not an offer.
  const offering = state.action === "join" && !state.blocked;
  const ctaLabel = offering && joining ? "Joining\u2026" : state.label;

  const onCta = () => {
    if (state.action === "join") return onJoin && onJoin();
    if (state.action === "leaderboard") {
      return navigate(standaloneLeaderboardPath(game?.vanity_url));
    }
    return navigate(`/contest/${game?.vanity_url}`);
  };

  return (
    <>
      {offering && (
        <p className="text-center text-sm font-normal text-secondary">
          {game?.is_cash_prize_league ? "Entry required" : "Free to join"}
        </p>
      )}
      <button
        type="button"
        onClick={onCta}
        disabled={state.blocked || (offering && (disabled || joining))}
        className={`${
          offering ? "mt-2" : ""
        } flex w-full items-center justify-center gap-2 rounded-xl bg-[#0F4146] py-3.5 font-semibold text-white transition-colors hover:bg-[#0B3A3F] disabled:opacity-60`}
      >
        {/* The invite glyph belongs to joining a league and to opening one. A
            leaderboard is neither, and this icon beside "View Leaderboard" would
            read as an invitation to join something. The two states it does fit
            keep it exactly as they had it. */}
        {state.action !== "leaderboard" && <InvitePeopleIcon className="w-5 h-5" />}
        {ctaLabel}
      </button>
    </>
  );
};

// The mobile CTA is docked over the viewport, `bottom-16` above the app's fixed
// 64px bottom navigation, and the page has to end far enough up that the last
// card scrolls clear of it. `DOCK_GAP` is the white left between the two.
const BOTTOM_NAV_H = 64;
const DOCK_GAP = 16;

// `identityInShell` / `showJoinCta` are how League details reuses these exact
// pages. Client: "I am inside a cash game > 'League details' from left menu bar.
// It has the Cash Game header but it has a public league landing page bottom. Do
// a pass on public, private, and cash games 'League details' should be linked to
// the correct landing page."
//
// It could not have been right: League details picked its own card, by its own
// rules, from its own copy of the private RuleCard — so a cash game fell into the
// public branch, and the private one drifted from the private landing every time
// that landing was revised. Both screens now go through THIS component, which is
// the one place that decides which of the three landing pages a competition gets.
// Two props are all the difference between them: the host names the league above
// the card, and a member has nothing to join.
// The content column a landing page is drawn in.
//
// Client: "Right now, the Game details screen is much narrower than the original
// Game landing page. The original game landing page is better. Can't the Game
// details screens be the same width as the original landing page? they should
// all three be identical minus the CTA for the details but same width."
//
// They were not, and the reason is that the width lived at the CALL SITE. The
// front door (LeagueEntry) gave a cash game 920px and everything else
// max-w-screen-md; League details hardcoded max-w-screen-md for all three, so a
// cash game's details screen was drawn 768px wide against its own 920px landing
// page — the two-column layout squeezed into three quarters of the column it was
// designed for.
//
// So the width now travels WITH the page rather than with whoever is hosting it.
// Both hosts call this, which is the only way "all three identical minus the
// CTA" can stay true after the next revision.
//
// Why the cash game is the exception: its desktop design is a two-column page
// split 580 + 300, drawn in a 920px column. At max-w-screen-md the rail leaves
// ~400px beside it, narrower than the phone its left column was drawn for.
// Public and private are single-column pages and keep the width the client set
// for them.
export const landingContainerClass = (game) =>
  `mx-auto px-4 md:px-6 ${
    game?.is_cash_prize_league ? "max-w-[920px]" : "max-w-screen-md"
  }`;

const LeagueJoinPreview = ({
  game,
  onJoined,
  titleInHeader = false,
  headerInHost = false,
  identityInShell = false,
  showJoinCta = true,
  // The desktop header row's share icon — LeaguePlayTopBar, at the right edge
  // of the content column. Client, on the previews: "move share icon down
  // inline with the arrow and align it to the right edge of the content."
  //
  // The details screen used to draw its own share in a focused header above the
  // card and pass `false` here so there would not be two. That header is gone,
  // so it passes the real answer instead — anyone may share a public league or a
  // cash game, a private league only its organiser.
  showShare = true,
}) => {
  const navigate = useNavigate();
  const [joining, setJoining] = useState(false);
  const [celebrating, setCelebrating] = useState(false);

  // How much room the docked mobile CTA needs beneath the card. Measured rather
  // than guessed so a longer label, a font swap or a second line corrects it —
  // the same treatment the Cash Game landing page gives its dock. Zero at md,
  // where the dock is display:none and the CTA is back in the flow.
  const dockRef = useRef(null);
  const [dockPad, setDockPad] = useState(BOTTOM_NAV_H + 88 + DOCK_GAP);

  useEffect(() => {
    const el = dockRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const measure = () => {
      const h = el.getBoundingClientRect().height;
      if (h > 0) setDockPad(Math.round(h) + BOTTOM_NAV_H + DOCK_GAP);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const isPrivate = game?.game_type === "private";
  const copy = isPrivate ? COPY.private : COPY.public;
  // The exact instants the league opens and closes — the same two labels the
  // public landing page prints, from the same helpers.

  const handleJoin = async () => {
    if (!game?.id) return;
    // A pending public league is not joinable, and the button is greyed out to
    // say so. This is the same rule enforced where it cannot be bypassed — a
    // stale card, a re-enabled button in devtools, a queued tap.
    if (isPendingApproval(game) && !game.isMember) return;
    if (game.isMember) {
      navigate(`/contest/${game.vanity_url}`);
      return;
    }
    setJoining(true);
    try {
      // A private league needs the invite code on the server too; the code
      // is on the game this screen was opened with.
      const res = await joingame({
        gameId: game.id,
        ...(isPrivate && game.invite_code ? { invite_code: game.invite_code } : {}),
      });

      // The join response used to be unable to confirm anything on its own: the
      // API wraps every outcome in SuccessReturn, so "Game not found." also
      // arrives as HTTP 200 with success:true. So this screen re-read the league
      // and checked isMember there instead.
      //
      // Client Rev 4: "This account still cannot join any leagues." That second
      // read is why one account could be stuck forever. get-game-from-slug is a
      // far heavier query than the join itself — it loads every member of the
      // league and each of their user records — and if it fails, the catch below
      // cannot tell "the join was refused" from "the confirmation call fell
      // over". Both come out as "Couldn't join the league", so a member who IS
      // enrolled is told they are not, every time they try, and retrying can
      // never help: the join returns ALREADY_MEMBER and the same read fails
      // again.
      //
      // join-game now says so itself (`data.isMember`), set only on the paths
      // that really do end in membership. That is the authoritative answer and it
      // comes from the request that did the work, so nothing else has to succeed
      // for a real join to be recognised.
      const joinedNow = res?.data?.success !== false && res?.data?.data?.isMember === true;

      // Fallback for a backend that predates that flag: ask the league, exactly as
      // before. Wrapped, because an unreachable confirmation must not turn a join
      // that already succeeded into an error message.
      let fresh = null;
      if (!joinedNow) {
        try {
          const check = await getGameFromSlug({ slug: game.vanity_url });
          fresh = check?.data?.success === false ? null : check?.data?.data;
        } catch (verifyErr) {
          console.error("League membership re-check failed:", verifyErr);
        }
      }

      // "You're in" must never appear before one of those two answers. A
      // celebration followed by an error is a lie the user watched happen.
      if (joinedNow || fresh?.isMember) {
        // Tell the app shell before anything navigates. The left rail, the 3-dot
        // menu, the bottom bar and the header all key off membership, and the URL
        // does not change when you join from a league's own front door — so
        // without this they keep the "not a member" answer they already had and
        // only catch up on a reload.
        // `fresh` is only present on the fallback path; on the normal path the
        // shell re-reads the league itself when it navigates.
        if (fresh) primeLeagueContext(game.vanity_url, fresh);

        // Celebrate, then drop the user inside the league.
        setCelebrating(true);
        setTimeout(() => {
          if (onJoined) onJoined();
          else navigate(`/contest/${game.vanity_url}`, { replace: true });
        }, CELEBRATION_MS);
      } else {
        toast.error(joinErrorMessage(res?.data?.message));
        setJoining(false);
      }
    } catch (e) {
      toast.error(joinErrorMessage(e?.response?.data?.message));
      setJoining(false);
    }
  };

  // Only a private league carries an organiser prize; what every league awards is
  // the podium badges, which have their own card.
  const rewardText = game?.reward ? stripHtml(game.reward).trim() : "";

  const celebration = (
    /* durationMs is this screen's own wait, so the modal knows how long it has
       before the league opens. isPrivate picks which artwork turns. */
    <LeagueJoinedCelebrationModal
      open={celebrating}
      title={game?.title}
      isPrivate={isPrivate}
      durationMs={CELEBRATION_MS}
    />
  );

  // A CASH GAME has its own landing page (client tickets 4746/4747). It is a
  // public league by `game_type`, so before this it fell through to the public
  // league card below — a page that pitches a league someone else is running and
  // has nowhere to put a prize breakdown, contest rules or an entrants count.
  // Checked before public/private for the same reason leagueTypeKey checks it
  // first: a cash game is a cash game before it is anything else.
  if (game?.is_cash_prize_league) {
    return (
      <>
        <CashGameLandingPage
          game={game}
          joining={joining}
          disabled={joining || celebrating}
          onJoin={handleJoin}
          identityInShell={identityInShell}
          showJoinCta={showJoinCta}
          showShare={showShare}
        />
        {celebration}
      </>
    );
  }

  if (!isPrivate) {
    return (
      <>
        {/* Client Rev4 (0014888 item 4): "I like the sticky cta. i want to make
            the public and private mobile landing pages' sticky cta as well...
            Public and private leagues share the same entry model, so their
            sticky components should match."

            They do now, by construction: this is the same JoinBlock inside the
            same dock the private branch renders below, which is itself the
            construction the client already signed off on the Cash Game page.
            The card's own CTA goes `hidden md:block` so a phone shows one CTA,
            not two. */}
        <div
          className="pb-[var(--invite-dock-pad)] md:pb-0"
          style={{ "--invite-dock-pad": `${showJoinCta ? dockPad : 0}px` }}
        >
          <PublicLeagueJoinCard
            game={game}
            joining={joining}
            disabled={joining || celebrating}
            onJoin={handleJoin}
            identityInShell={identityInShell}
            showJoinCta={showJoinCta}
            showShare={showShare}
          />
        </div>

        {showJoinCta && (
          <div
            ref={dockRef}
            className="fixed inset-x-0 bottom-16 z-40 border-t border-gray-200 bg-white/95 px-4 pb-3 pt-2 backdrop-blur-md md:hidden"
          >
            <div className="mx-auto max-w-md">
              <JoinBlock
                game={game}
                joining={joining}
                disabled={joining || celebrating}
                onJoin={handleJoin}
              />
            </div>
          </div>
        )}
        {celebration}
      </>
    );
  }

  return (
    <>
      {/* The docked CTA is out of the flow, so the page has to end above it or the
          Organizer prize card sits behind the bar. `--dock-pad` is the measured
          dock height plus the bottom navigation beneath it; at md the dock is
          display:none and the CTA is back inside the card, so there is nothing to
          reserve. */}
      <div
        className="pb-[var(--invite-dock-pad)] md:pb-0"
        style={{ "--invite-dock-pad": `${showJoinCta ? dockPad : 0}px` }}
      >
      <LeagueInvitePreviewCard
        game={game}
        titleInHeader={titleInHeader}
        headerInHost={headerInHost}
        identityInShell={identityInShell}
        showShare={showShare}
        rules={
          <RuleCard
            icon={copy.icon}
            title={copy.title}
            lead={copy.lead}
            /* Client: "The details card should contain six items: STARTS / ENDS
               / STARTING CASH / TRADE LIMIT / JOIN AFTER START / PORTFOLIO
               VISIBILITY", two columns and three rows on desktop, stacked on
               mobile, "Match the public league landing page layout."

               Literally the public landing page's own block — the same component,
               not a copy of it — so the two pages cannot describe a league's
               schedule or its rules differently, which is what they did before
               the schedule rows were first shared.

               This replaces the three check rows the card used to carry. Two of
               them (JOIN AFTER START, PORTFOLIO VISIBILITY) are in the six, in
               the public page's wording; the third, `copy.closing` — "Only
               members can see inside" — has no slot in the client's list and is
               no longer printed on this page. It is still the private league's
               defining promise, so it is worth putting back to them rather than
               assuming the omission was deliberate. */
            details={<LeagueDetailRows game={game} />}
          />
        }
        reward={
          /* The private league's landing page. Client (badge asset drop):
             "Besides the different badges, all three landing pages 'Build your
             case' section should be identical" — so this takes the same
             "landing" copy set as the cash game and public league pages, and
             only the artwork differs. */
          /* Client asked for the longer subtitle when this card went full width,
             and I applied it at BOTH breakpoints because that message set no
             limit. That was wrong, and they corrected it: "Mobile subtitle
             wraps. I am sorry; I never intended for the mobile subtitle to be
             changed, just the desktop, now that the desktop design has the
             space. Please change the private league landing page subtitle back
             to: Top 3 finishes add to your trophy case."

             So it is `subheadingDesktop` now — exactly how the public landing
             page carries the same sentence. Mobile falls through to the shared
             PODIUM_COPY.landing line, which is the string the client named, so
             the two landing pages are identical at both widths again. */
          <PodiumRewardsCard
            variantKey={isPrivate ? "private" : "public"}
            surface="landing"
            subheadingDesktop="A top 3 finish adds a badge to your trophy case."
            tinted
          />
        }
        extra={
          isPrivate && rewardText ? (
            <div className="rounded-2xl border border-gray-200 bg-[#F8FAFA] p-4 md:p-5 flex items-start justify-between gap-4">
              <div className="min-w-0">
                {/* Peer of Private League and Podium rewards — client Rev 2 names
                    all three as peer sections that "should share a heading token",
                    so this moves off text-sm onto the same text-base as the other
                    two. */}
                <h3 className="font-montserrat font-bold text-base text-[#0F172A]">
                  Organizer prize
                </h3>
                <p className="mt-1 text-sm text-[#0F172A]">{rewardText}</p>
              </div>
              <TrophyOutlineIcon className="w-7 h-7 shrink-0" stroke="#22A36B" />
            </div>
          ) : null
        }
        footer={
          /* DESKTOP only — the mobile copy of this lives in the docked bar below,
             which is why this is `hidden md:block` rather than the one footer it
             used to be.

             Client Rev 2 had put this at "about 300px wide", centred. Client now:
             "Make the CTA the same width as the content cards. Add 'Free to Join'
             above the CTA, just like the public leagues CTA." The footer already
             sits in the card's `md:px-8`, the same box the rules/reward grid does,
             so a full-width button IS the content-card width — no measurement and
             nothing to keep in sync if that padding ever changes. */
          showJoinCta ? (
            <div className="hidden md:block">
              <JoinBlock
                game={game}
                joining={joining}
                disabled={joining || celebrating}
                onJoin={handleJoin}
              />
            </div>
          ) : null
        }
      />
      </div>

      {/* MOBILE sticky CTA. Client: "Remove 'Not now' from under the CTA. The back
          arrow is sticky in the header and can serve this purpose." — so the
          secondary action is gone at both widths, and the header's back arrow is
          the way out. "Make Free to join and the CTA sticky on mobile landing
          page" is this dock.

          Same construction as the Cash Game landing page's dock, which the client
          already signed off: `bottom-16` clears the app's fixed 64px bottom
          navigation rather than sitting on top of it, and the page reserves the
          dock's measured height below its own content so the last card can always
          scroll clear. Copying that rather than inventing a second sticky idiom
          keeps the two landing pages behaving the same way on a phone. */}
      {showJoinCta && (
        <div
          ref={dockRef}
          className="fixed inset-x-0 bottom-16 z-40 border-t border-gray-200 bg-white/95 px-4 pb-3 pt-2 backdrop-blur-md md:hidden"
        >
          <div className="mx-auto max-w-md">
            <JoinBlock
              game={game}
              joining={joining}
              disabled={joining || celebrating}
              onJoin={handleJoin}
            />
          </div>
        </div>
      )}

      {celebration}
    </>
  );
};

export default LeagueJoinPreview;
